import React, { useState } from "react";
import {
  Play,
  FileText,
  Image as ImageIcon,
  File,
  Music,
  ExternalLink,
  Edit,
  Eye,
  Calendar,
  Trash2,
  AlertTriangle,
} from "lucide-react";
import { format } from "date-fns";
import ImageComponent from "./ImageComponent";
import { apiClient, BASE_URL } from "@/config/api.config";
function ContentCard({ content, onEdit, apiBaseUrl, onDelete }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Define a base URL for API calls - try to determine dynamically if not provided
  const BASE_URL = React.useMemo(() => {
    if (apiBaseUrl) return apiBaseUrl;

    // Try to get the API URL from environment or window if available
    if (typeof window !== "undefined") {
      // Check if there's an API URL in localStorage (you might set this during login)
      const storedApiUrl = localStorage.getItem("apiBaseUrl");
      if (storedApiUrl) return storedApiUrl;

      // Fallback to current origin
      return window.location.origin;
    }

    // Final fallback
    return "http://localhost:5000";
  }, [apiBaseUrl]);

  const getContentIcon = (type) => {
    switch (type) {
      case "video":
        return <Play className="h-8 w-8" />;
      case "paragraph":
        return <FileText className="h-8 w-8" />;
      case "image":
        return <ImageIcon className="h-8 w-8" />;
      case "document":
        return <File className="h-8 w-8" />;
      case "audio":
        return <Music className="h-8 w-8" />;
      case "videoLink":
        return <ExternalLink className="h-8 w-8" />;
      default:
        return <File className="h-8 w-8" />;
    }
  };

  // Enhanced URL resolution function
  const resolveFileUrl = (fileUrl) => {
    if (!fileUrl) return null;

    // For absolute URLs (including protocol) - use as is
    if (/^https?:\/\//i.test(fileUrl)) {
      return fileUrl;
    }

    // For relative URLs, ensure they start with a single slash
    const normalizedFileUrl = fileUrl.replace(/^\/+/, "");
    const cleanBaseUrl = baseUrl.replace(/\/+$/, "");

    return `${cleanBaseUrl}/${normalizedFileUrl}`;
  };

  const handleDeleteClick = () => {
    setShowDeleteConfirm(true);
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setDeleteError(null);
  };

  const confirmDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      const token = localStorage.getItem("authToken");

      if (!token) {
        throw new Error("Authentication token not found. Please log in.");
      }

      // Use apiClient from your config instead of fetch
      const response = await apiClient.delete(`/admin/delete/${content._id}`);

      if (onDelete) {
        onDelete(content._id);
      }

      setShowDeleteConfirm(false);
    } catch (err) {
      setDeleteError(
        err.response?.data?.message || err.message || "Failed to delete content"
      );
      console.error("Error deleting content:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const ContentPreview = ({ content }) => {
    if (!content) {
      return (
        <div className="h-48 bg-gray-100 flex items-center justify-center">
          <div className="text-center text-gray-400">
            <File className="h-12 w-12 mx-auto" />
            <p className="mt-2 text-sm">No content available</p>
          </div>
        </div>
      );
    }

    // Determine which content type to use for rendering
    const typeToUse = content.contentType || content.displayType;

    switch (typeToUse) {
      case "image": {
        // Enhanced logic to find image URL from various possible properties
        let imageUrl = null;

        // Collect all possible places where the image URL might be stored
        const possibleUrlFields = [
          content.fileUrl,
          content.displayUrl,
          content.imageUrl,
          content.url,
          content.thumbnailUrl,
          content.src,
        ];

        // Find the first non-empty value
        imageUrl = possibleUrlFields.find(
          (url) => url && typeof url === "string"
        );

        return (
          <div className="h-48 bg-gray-100 relative">
            <ImageComponent
              imageUrl={imageUrl}
              apiBaseUrl={BASE_URL}
              alt={content.title || "Image content"}
              fallbackImageUrl="/api/placeholder/400/300"
            />
          </div>
        );
      }

      case "video":
        return (
          <div className="relative h-48 bg-black flex items-center justify-center">
            {content.fileUrl ? (
              <video
                src={resolveFileUrl(content.fileUrl)}
                className="max-h-48 w-full object-contain"
                controls
                poster={
                  content.thumbnailUrl
                    ? resolveFileUrl(content.thumbnailUrl)
                    : undefined
                }
                onError={(e) => {
                  console.error("Video failed to load:", e.target.src);
                }}
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-400">
                <Play className="h-12 w-12" />
                <p className="text-sm mt-2">Video not available</p>
              </div>
            )}
          </div>
        );

      case "audio":
        return (
          <div className="h-24 bg-gray-100 flex items-center justify-center p-4">
            {content.fileUrl ? (
              <audio
                src={resolveFileUrl(content.fileUrl)}
                className="w-full"
                controls
                onError={(e) =>
                  console.error("Audio failed to load:", e.target.src)
                }
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-400">
                <Music className="h-12 w-12" />
                <p className="text-sm mt-2">Audio not available</p>
              </div>
            )}
          </div>
        );

      case "videoLink": {
        const getEmbedUrl = (url) => {
          if (!url) return "";

          try {
            if (url.includes("youtube.com/watch")) {
              const urlObj = new URL(url);
              const videoId = urlObj.searchParams.get("v");
              if (videoId) return `https://www.youtube.com/embed/${videoId}`;
            }

            if (url.includes("youtu.be/")) {
              const videoId = url.split("youtu.be/")[1]?.split(/[?&#]/)[0];
              if (videoId) return `https://www.youtube.com/embed/${videoId}`;
            }

            if (url.includes("vimeo.com/")) {
              const videoId = url.split("vimeo.com/")[1]?.split(/[?&#]/)[0];
              if (videoId) return `https://player.vimeo.com/video/${videoId}`;
            }

            return url;
          } catch (error) {
            console.error("Error parsing video URL:", error);
            return url;
          }
        };

        return (
          <div className="relative h-48 bg-black flex items-center justify-center">
            {content.videoLink ? (
              <iframe
                src={getEmbedUrl(content.videoLink)}
                className="w-full h-full"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title={content.title || "Video embed"}
                onError={(e) =>
                  console.error(
                    "Video embed failed to load:",
                    content.videoLink
                  )
                }
              />
            ) : (
              <div className="flex flex-col items-center justify-center text-gray-400">
                <ExternalLink className="h-12 w-12" />
                <p className="text-sm mt-2">No video link available</p>
              </div>
            )}
          </div>
        );
      }

      case "paragraph":
        return (
          <div className="h-48 bg-gray-50 p-4 overflow-hidden">
            {content.paragraphContent ? (
              <p className="line-clamp-6 text-sm">{content.paragraphContent}</p>
            ) : (
              <p className="text-sm text-gray-400 italic">
                No content available
              </p>
            )}
          </div>
        );

      case "document":
        return (
          <div className="h-48 bg-gray-100 flex items-center justify-center">
            <div className="text-center">
              <File className="h-12 w-12 mx-auto text-gray-600" />
              <p className="mt-2 text-sm truncate max-w-xs">
                {content.fileUrl
                  ? content.fileUrl.split("/").pop()
                  : "Document"}
              </p>
              {content.fileUrl && (
                <a
                  href={resolveFileUrl(content.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 text-blue-500 hover:underline text-sm inline-block"
                >
                  Download
                </a>
              )}
            </div>
          </div>
        );

      default:
        return (
          <div className="h-48 bg-gray-100 flex items-center justify-center">
            <div className="text-center text-gray-500">
              {getContentIcon(typeToUse)}
              <p className="mt-2">{typeToUse || "Unknown type"}</p>
              {content.fileUrl && (
                <a
                  href={resolveFileUrl(content.fileUrl)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 text-blue-500 hover:underline text-sm inline-block"
                >
                  View File
                </a>
              )}
            </div>
          </div>
        );
    }
  };

  const DeleteConfirmModal = () => {
    if (!showDeleteConfirm) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-lg max-w-md w-full p-6">
          <div className="flex items-center text-red-500 mb-4">
            <AlertTriangle className="h-6 w-6 mr-2" />
            <h3 className="text-lg font-bold">Confirm Deletion</h3>
          </div>

          {deleteError && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
              {deleteError}
            </div>
          )}

          <p className="mb-4">
            Are you sure you want to delete{" "}
            <span className="font-bold">{content.title}</span>? This action
            cannot be undone.
          </p>

          <div className="flex justify-end gap-3">
            <button
              onClick={cancelDelete}
              className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              onClick={confirmDelete}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 flex items-center"
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <svg
                    className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (!content) {
    return (
      <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 h-64 flex items-center justify-center">
        <p className="text-gray-400">Content not available</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 transition-all hover:shadow-lg">
      <ContentPreview content={content} />

      <div className="p-4">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <span
              className={`px-2 py-1 text-xs rounded-full ${
                content.status === "published"
                  ? "bg-green-100 text-green-800"
                  : content.status === "draft"
                  ? "bg-yellow-100 text-yellow-800"
                  : "bg-gray-100 text-gray-800"
              }`}
            >
              {content.status || "unknown"}
            </span>

            <span className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
              {content.contentType || content.displayType || "unknown"}
            </span>
          </div>

          <div className="flex items-center gap-1">
            <button
              onClick={() => onEdit && onEdit(content)}
              className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-full"
              title="Edit"
            >
              <Edit className="h-4 w-4" />
            </button>
            <button
              onClick={handleDeleteClick}
              className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-full"
              title="Delete"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        </div>

        <h3 className="font-bold text-lg mb-1 truncate">
          {content.title || "Untitled"}
        </h3>

        {content.description && (
          <p className="text-gray-600 text-sm line-clamp-2 mb-3">
            {content.description}
          </p>
        )}

        <div className="flex justify-between text-xs text-gray-500 mt-2">
          {/* <div className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {content.createdAt
              ? format(new Date(content.createdAt), "MMM dd, yyyy")
              : "No date"}
          </div> */}
        </div>

        {content.tags && content.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-3">
            {content.tags.slice(0, 3).map((tag, index) => (
              <span
                key={index}
                className="bg-gray-100 px-2 py-1 rounded-full text-xs text-gray-600"
              >
                #{tag}
              </span>
            ))}
            {content.tags.length > 3 && (
              <span className="bg-gray-100 px-2 py-1 rounded-full text-xs text-gray-600">
                +{content.tags.length - 3}
              </span>
            )}
          </div>
        )}
      </div>

      <DeleteConfirmModal />
    </div>
  );
}
export default ContentCard;
